import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProgress } from '../contexts/ProgressContext';
import { 
    getSectionById, 
    getLessonsBySection, 
    getLessonsByModule,
    getModulesBySection,
    lessons 
} from '../data/courseData';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import { 
    BookOpen, 
    Compass, 
    Flame, 
    Sparkles, 
    ArrowLeft,
    Star,
    ChevronRight,
    Play,
    Trophy
} from 'lucide-react';

const HERO_BG = "https://customer-assets.emergentagent.com/job_gamma-to-web/artifacts/brgxn5xe_Grok%20Image%202026-02-05%20at%2017.41.51.png";

const iconMap = {
    'book-open': BookOpen,
    'compass': Compass,
    'flame': Flame,
    'sparkles': Sparkles
};

const colorClasses = {
    primary: {
        bg: 'bg-primary/10',
        border: 'border-primary/30',
        text: 'text-primary',
        glow: 'hover:shadow-gold'
    },
    mystical: {
        bg: 'bg-mystical/10',
        border: 'border-mystical/30',
        text: 'text-mystical-glow',
        glow: 'hover:shadow-mystical'
    },
    accent: {
        bg: 'bg-accent/10',
        border: 'border-accent/30',
        text: 'text-accent-glow',
        glow: 'hover:shadow-[0_4px_24px_-4px_hsl(350_60%_45%/0.4)]'
    },
    emerald: {
        bg: 'bg-emerald/10',
        border: 'border-emerald/30',
        text: 'text-emerald',
        glow: 'hover:shadow-[0_4px_24px_-4px_hsl(160_45%_35%/0.4)]'
    }
};

const LessonItem = ({ lesson, colors, isComplete, onClick }) => (
    <Card
        className={`group cursor-pointer card-mystical ${colors.glow}`}
        onClick={onClick}
    >
        <CardContent className="p-4">
            <div className="flex items-center gap-4">
                {/* Lesson Number / Status */}
                <div className={`
                    w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                    transition-all duration-300
                    ${isComplete 
                        ? 'bg-primary/20 border border-primary/50' 
                        : `${colors.bg} border ${colors.border}`
                    }
                `}>
                    {isComplete ? (
                        <Star className="w-4 h-4 text-primary fill-primary star-complete" />
                    ) : (
                        <span className={`font-display text-sm ${colors.text}`}>
                            {lesson.order}
                        </span>
                    )}
                </div>

                {/* Lesson Info */}
                <div className="flex-1 min-w-0">
                    <h3 className="font-display text-base font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                        {lesson.title}
                    </h3>
                    <p className="font-body text-sm text-muted-foreground truncate">
                        {lesson.subtitle}
                    </p>
                </div>

                {/* Action */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    {isComplete && (
                        <span className="hidden sm:inline text-xs font-ui text-primary bg-primary/10 px-2 py-1 rounded-full">
                            ✓
                        </span>
                    )}
                    {lesson.isFinal && (
                        <Trophy className="w-4 h-4 text-primary" />
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="group-hover:bg-primary/10 group-hover:text-primary h-8 w-8"
                    >
                        {isComplete ? (
                            <ChevronRight className="w-4 h-4" />
                        ) : (
                            <Play className="w-4 h-4" />
                        )}
                    </Button>
                </div>
            </div>
        </CardContent>
    </Card>
);

const SectionPage = () => {
    const { sectionId } = useParams();
    const navigate = useNavigate();
    const { isLessonComplete, getSectionProgress } = useProgress();

    const section = getSectionById(sectionId);
    const sectionLessons = getLessonsBySection(sectionId);
    const sectionModules = getModulesBySection(sectionId);
    const progress = getSectionProgress(sectionId, lessons);
    const hasModules = sectionModules.length > 0;

    if (!section) {
        return (
            <div className="min-h-screen relative flex items-center justify-center">
                <div 
                    className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${HERO_BG})` }}
                />
                <div className="fixed inset-0 bg-background/80" />
                <Card className="bg-card/80 backdrop-blur-sm relative z-10">
                    <CardContent className="p-8 text-center">
                        <h2 className="font-display text-xl text-foreground mb-4">Section Not Found</h2>
                        <Button variant="gold" onClick={() => navigate('/dashboard')}>
                            Return to Dashboard
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const Icon = iconMap[section.icon] || BookOpen;
    const colors = colorClasses[section.color] || colorClasses.primary;

    const handleLessonClick = (lessonId) => {
        navigate(`/lesson/${lessonId}`);
    };

    // Get module progress
    const getModuleProgress = (moduleId) => {
        const moduleLessons = getLessonsByModule(moduleId);
        const completed = moduleLessons.filter(l => isLessonComplete(l.id)).length;
        return { completed, total: moduleLessons.length };
    };

    return (
        <div className="min-h-screen relative">
            {/* Background image */}
            <div 
                className="fixed inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${HERO_BG})` }}
            />
            <div className="fixed inset-0 bg-background/80" />
            
            {/* Header */}
            <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50 relative">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center gap-4">
                        <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate('/dashboard')}
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span className="hidden sm:inline">Dashboard</span>
                        </Button>
                        <div className="h-6 w-px bg-border" />
                        <div className="flex items-center gap-2">
                            <div className={`p-1.5 rounded ${colors.bg} border ${colors.border}`}>
                                <Icon className={`w-4 h-4 ${colors.text}`} />
                            </div>
                            <span className="font-ui text-sm text-muted-foreground">
                                Section {section.number}
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 relative z-10">
                {/* Section Header */}
                <div className="mb-10 fade-in">
                    <div className="flex items-start gap-4 mb-6">
                        <div className={`p-4 rounded-xl ${colors.bg} border ${colors.border}`}>
                            <Icon className={`w-8 h-8 ${colors.text}`} />
                        </div>
                        <div className="flex-1">
                            <p className={`font-display text-sm ${colors.text} mb-1`}>
                                Section {section.number}
                            </p>
                            <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                                {section.title}
                            </h1>
                            <p className="font-body text-lg italic text-muted-foreground">
                                {section.subtitle}
                            </p>
                        </div>
                    </div>

                    <p className="font-body text-base text-muted-foreground mb-6 max-w-3xl">
                        {section.description}
                    </p>

                    {/* Progress Bar */}
                    <div className="max-w-md">
                        <div className="flex items-center justify-between mb-2">
                            <span className="font-ui text-sm text-muted-foreground">
                                Section Progress
                            </span>
                            <span className="font-ui text-sm font-medium text-primary">
                                {progress.completed} / {progress.total} Lessons
                            </span>
                        </div>
                        <Progress 
                            value={progress.percentage} 
                            className="h-2 bg-muted"
                        />
                    </div>
                </div>

                {/* Content - With or without modules */}
                {hasModules ? (
                    // Sections with modules (II and III)
                    <div className="space-y-4">
                        <h2 className="font-display text-xl text-foreground mb-4">
                            Modules & Lessons
                        </h2>
                        
                        <Accordion type="multiple" className="space-y-3" defaultValue={sectionModules.map(m => m.id)}>
                            {sectionModules.map((module, moduleIndex) => {
                                const moduleLessons = getLessonsByModule(module.id);
                                const moduleProgress = getModuleProgress(module.id);
                                
                                return (
                                    <AccordionItem 
                                        key={module.id} 
                                        value={module.id}
                                        className="border border-border/50 rounded-lg bg-card/50 overflow-hidden"
                                    >
                                        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-card/80 group">
                                            <div className="flex items-center gap-3 flex-1">
                                                <div className={`w-8 h-8 rounded flex items-center justify-center ${colors.bg} border ${colors.border}`}>
                                                    <span className={`font-display text-xs ${colors.text}`}>
                                                        {moduleIndex + 1}
                                                    </span>
                                                </div>
                                                <div className="text-left flex-1">
                                                    <h3 className="font-display text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                                                        {module.title}
                                                    </h3>
                                                    <p className="font-body text-sm text-muted-foreground">
                                                        {moduleProgress.completed}/{moduleProgress.total} lessons completed
                                                    </p>
                                                </div>
                                                {moduleProgress.completed === moduleProgress.total && moduleProgress.total > 0 && (
                                                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 border border-primary/30 mr-2">
                                                        <Star className="w-3 h-3 text-primary fill-primary" />
                                                        <span className="text-xs font-ui text-primary">Complete</span>
                                                    </div>
                                                )}
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="px-4 pb-4">
                                            <div className="space-y-2 pt-2">
                                                {moduleLessons.map((lesson) => (
                                                    <LessonItem
                                                        key={lesson.id}
                                                        lesson={lesson}
                                                        colors={colors}
                                                        isComplete={isLessonComplete(lesson.id)}
                                                        onClick={() => handleLessonClick(lesson.id)}
                                                    />
                                                ))}
                                            </div>
                                        </AccordionContent>
                                    </AccordionItem>
                                );
                            })}
                        </Accordion>
                    </div>
                ) : (
                    // Sections without modules (I and IV)
                    <div className="space-y-4">
                        <h2 className="font-display text-xl text-foreground mb-4">
                            Lessons
                        </h2>
                        
                        <div className="space-y-3">
                            {sectionLessons.map((lesson, index) => (
                                <div 
                                    key={lesson.id}
                                    className="fade-in"
                                    style={{ animationDelay: `${index * 0.05}s` }}
                                >
                                    <LessonItem
                                        lesson={lesson}
                                        colors={colors}
                                        isComplete={isLessonComplete(lesson.id)}
                                        onClick={() => handleLessonClick(lesson.id)}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Navigation */}
                <div className="mt-10 flex justify-center">
                    <Button 
                        variant="codex" 
                        onClick={() => navigate('/dashboard')}
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to All Sections
                    </Button>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-border/50 bg-card/30 backdrop-blur-sm mt-16 relative z-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
                    <p className="font-ui text-sm text-muted-foreground">
                        &copy; {new Date().getFullYear()} Gringos English. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default SectionPage;
